import { useHouseholds } from '@platewell/query';
import { MAX_HOUSEHOLD_NAME_LENGTH } from '@platewell/schema';
import { useEffect, useState } from 'react';
import { renameHousehold } from '../../lib/api.js';

/** MaterialIcons "edit", inline like the other glyphs since the web app has no icon library. */
const PENCIL_PATH =
  'M3 17.25V21h3.75L17.81 9.94l-3.75-3.75zM20.71 7.04a.996.996 0 0 0 0-1.41l-2.34-2.34a.996.996 0 0 0-1.41 0l-1.83 1.83 3.75 3.75z';

/**
 * The settings page heading, which owners can edit in place. It stands in for a separate
 * rename section, so the name is changed exactly where it is read.
 */
export function HouseholdName() {
  const { active, activeHouseholdId, reloadHouseholds } = useHouseholds();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A rename half-typed for one household must not carry over to the next.
  useEffect(() => {
    setEditing(false);
    setError(null);
  }, [activeHouseholdId]);

  const name = active?.name ?? 'Household';
  const trimmed = value.trim();
  const canSave = !saving && trimmed !== '' && trimmed !== active?.name;

  function cancel() {
    setEditing(false);
    setError(null);
  }

  async function save() {
    if (activeHouseholdId == null || !canSave) return;
    setSaving(true);
    setError(null);
    try {
      await renameHousehold(activeHouseholdId, trimmed);
      // The name lives on the household list, which also feeds the switcher.
      await reloadHouseholds();
      setEditing(false);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not rename the household');
    } finally {
      setSaving(false);
    }
  }

  if (!editing) {
    return (
      <div className="household-name">
        <h1>{name}</h1>
        {/* Owners only, as the server refuses anyone else. */}
        {active?.role === 'owner' && (
          <button
            type="button"
            className="household-name-edit"
            aria-label="Rename household"
            title="Rename household"
            onClick={() => {
              setValue(active.name);
              setEditing(true);
            }}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path d={PENCIL_PATH} fill="currentColor" />
            </svg>
          </button>
        )}
      </div>
    );
  }

  return (
    <>
      <form
        className="household-name"
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
      >
        <input
          className="input household-name-input"
          value={value}
          maxLength={MAX_HOUSEHOLD_NAME_LENGTH}
          disabled={saving}
          autoFocus
          aria-label="Household name"
          onFocus={(event) => event.currentTarget.select()}
          onChange={(event) => setValue(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Escape') cancel();
          }}
        />
        <button type="submit" className="btn primary small" disabled={!canSave}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button type="button" className="btn ghost small" disabled={saving} onClick={cancel}>
          Cancel
        </button>
      </form>
      {error != null && <p className="error-text">{error}</p>}
    </>
  );
}
